import { useCallback, useEffect } from "react";
import eligibilityCheckServices from "../../../../services/api/service/eligibilityCheckService";
import { decrypt } from "../../../../utils/crypto";
import { setStatusPopup } from "../../../../services/redux/slices/statusPopupSlice";
import {
  setAggregatorDocId,
  setConsentApplicationNo,
  setDocumentApplicantType,
} from "../../../../services/redux/slices/stepperDynamicFormSlice";

export function useAccountAggregator({
  dispatch,
  navigate,
  locationSearch,
  applicationNumber,
  tabIndex,
  userProfile,
  aggregatorDocId,
  consentApplicationNo,
  setAggregatorLoader,
  refreshDocuments,
}: {
  dispatch: any;
  navigate: any;
  locationSearch: string;
  applicationNumber: any;
  tabIndex: number;
  userProfile: any;
  aggregatorDocId: any;
  consentApplicationNo: any;
  setAggregatorLoader: (next: boolean) => void;
  refreshDocuments: () => void;
}) {
  const handleAccountAggregatorRequest = useCallback(
    async (id: number) => {
      try {
        setAggregatorLoader(true);
        dispatch(setDocumentApplicantType("Applicant"));

        const payload = {
          applicationNo: applicationNumber,
          name: userProfile?.fullName,
          mobile: decrypt(userProfile?.phoneNumber),
          email: decrypt(userProfile?.email),
          fromDate: new Date().toISOString(),
          toDate: new Date(
            new Date().setFullYear(new Date().getFullYear() + 1)
          ).toISOString(),
          redirectLink: `${window.location.origin}/assisted?trackingId=${applicationNumber}&no=${
            locationSearch?.split("=")?.[2]
          }`,
        };

        const response = await eligibilityCheckServices.accountAggregatorRequest(
          payload
        );
        if (response?.status === 200) {
          dispatch(
            setConsentApplicationNo(
              response?.data?.data?.data?.consentApplicationNo
            )
          );
          dispatch(setAggregatorDocId(id));

          let url = response?.data?.data?.data?.link;
          if (url && !/^https?:\/\//i.test(url)) {
            url = "https://" + url;
          }
          window.location.href = url;
        }
      } catch (error: any) {
        dispatch(
          setStatusPopup({
            status: "error",
            message: error?.response?.data?.message,
          })
        );
      } finally {
        setAggregatorLoader(false);
      }
    },
    [
      applicationNumber,
      userProfile,
      dispatch,
      locationSearch,
      setAggregatorLoader,
    ]
  );

  const handleAccountAggregatorRequestConsent = useCallback(async () => {
    setAggregatorLoader(true);
    try {
      const payload = {
        docId: aggregatorDocId,
        typeOfApplicant: tabIndex === 0 ? "Applicant" : `Co-Applicant ${tabIndex}`,
        applicationId: consentApplicationNo || "AAG20250729489",
      };

      const currentSearch = locationSearch || "";
      if (currentSearch.includes("consent=true")) {
        const newSearch = currentSearch.replace(/[?&]consent=true/g, "");
        const cleanSearch = newSearch.replace(/^\?&/, "?").replace(/^&/, "?");
        const newUrl = `${window.location.pathname}${cleanSearch}`;
        navigate(newUrl, { replace: true });
      }

      let response: any =
        await eligibilityCheckServices.accountAggregatorRequestConsent(payload);

      if (response?.status === 200) {
        if (!response?.data?.data?.fiDetailsVal?.[0]?.path) {
          await new Promise((resolve) => setTimeout(resolve, 2000));
          const retryResponse =
            await eligibilityCheckServices.accountAggregatorRequestConsent(payload);
          if (!retryResponse?.data?.data?.fiDetailsVal?.[0]?.path) {
            dispatch(
              setStatusPopup({
                status: "error",
                message: "Not able to get data from account aggregator",
              })
            );
            setAggregatorLoader(false);
            return;
          }
          response = retryResponse;
        }

        setTimeout(() => {
          refreshDocuments();
          dispatch(
            setStatusPopup({
              status: "success",
              message: "Document shared via Account Aggregator successfully",
            })
          );
          setAggregatorLoader(false);
        }, 5000);
      }
    } catch (error: any) {
      setAggregatorLoader(false);
      dispatch(
        setStatusPopup({
          status: "error",
          message: error?.response?.data?.message,
        })
      );
    }
  }, [
    aggregatorDocId,
    consentApplicationNo,
    dispatch,
    locationSearch,
    navigate,
    refreshDocuments,
    setAggregatorLoader,
    tabIndex,
  ]);

  useEffect(() => {
    let searchString = locationSearch || "";
    if (searchString.includes("?")) {
      const parts = searchString.split("?");
      if (parts.length > 2) {
        searchString = parts[0] + "?" + parts.slice(1).join("&");
      }
    }

    const params = new URLSearchParams(searchString);
    const consentStatus = params.get("consent");
    if (consentStatus === "true" && tabIndex !== -1) {
      handleAccountAggregatorRequestConsent();
    }
  }, [locationSearch, tabIndex, handleAccountAggregatorRequestConsent]);

  return {
    handleAccountAggregatorRequest,
  };
}

